/**
 * Content Data
 *
 * Static content for all home page sections
 * Based on spec.md and data-model.md
 */

import {
  HeroContent,
  MissionContent,
  EmailSubscriptionContent,
  DonationContent,
  WhyEqualParentingContent,
  ResourcesContent,
  CommunityContent,
  FooterContent,
  ImageCarouselContent,
  LegalOrganizationInfo,
} from './types'
import heroImage from '@/assets/hero.webp'
import {
  childMentalHealthStatistic,
  povertyReductionStatistic,
} from './statistics'
import image1 from '@/assets/homeCarousel/1.jpeg'
import image2 from '@/assets/homeCarousel/2.jpeg'
import image3 from '@/assets/homeCarousel/3.jpeg'
import image4 from '@/assets/homeCarousel/4.jpeg'
import image5 from '@/assets/homeCarousel/5.jpeg'
import image6 from '@/assets/homeCarousel/6.jpeg'
import image7 from '@/assets/homeCarousel/7.jpeg'
import image8 from '@/assets/homeCarousel/8.jpeg'
import image9 from '@/assets/homeCarousel/9.jpeg'
import image10 from '@/assets/homeCarousel/10.jpeg'

export const legalOrganizationInfo: LegalOrganizationInfo = {
  taxStatus: 'IRS-recognized 501(c)(3) nonprofit',
  ein: '39-4627638',
  legalAddress: '2120 Carey Ave, Cheyenne, WY 82001',
  legalAddressNote: 'Not for walk-in visits or mail correspondence.',
}

export const taxExemptDonationDisclaimer =
  'National Council for Equal Shared Parenting is an IRS-recognized 501(c)(3) nonprofit organization. Contributions are tax-deductible to the extent allowed by law. EIN: 39-4627638.'

// ============================================================================
// Hero Section Content
// ============================================================================

export const heroContent: HeroContent = {
  headline: 'Because children deserve a relationship with both parents',
  subheadline:
    "We're advocating for equal shared parenting rights, ensuring children maintain healthy relationships with both fit, willing, and able parents after divorce or separation.",
  heroImage: {
    src: heroImage,
    alt: 'Happy children with both parents showing diversity',
    fallbackSrc: heroImage,
  },
}

// ============================================================================
// Mission Section Content
// ============================================================================

export const missionContent: MissionContent = {
  title: 'Our Mission',
  content:
    "The National Council for Equal Shared Parenting is dedicated to raising awareness of the critical need for children to maintain strong, healthy relationships with both parents following divorce or separation. Through advocacy, education, and community building, we work toward legislative changes that prioritize children's well-being and recognize the importance of both parents in their lives. We believe that when both fit, willing, and able parents remain actively involved, children thrive emotionally, socially, and academically. Our mission is to transform family law to reflect the overwhelming research showing that children benefit immensely from equal time with both parents, and to support families navigating these challenges with evidence-based resources and compassionate guidance.",
  leadershipPreview: {
    name: 'Mark Ludwig',
    title: 'Founder & President',
    photoSrc: '/images/mark-ludwig.webp',
    photoAlt: 'Mark Ludwig, Founder and President of NCESP',
    bioTeaser:
      'Mark has a 40-year background in the political arena as a candidate for US Congress, campaign manager, advisor, and trainer.',
  },
}

// ============================================================================
// Email Subscription Section Content
// ============================================================================

export const emailSubscriptionContent: EmailSubscriptionContent = {
  title: 'Stay Connected',
  description:
    'Join thousands of parents, advocates, and supporters working toward equal shared parenting. Receive updates on legislative efforts, success stories, research findings, and opportunities to make a difference in your community.',
  subscriberCount: 1250, // Static value updated manually
  privacyStatement:
    'We respect your privacy and will never share your information',
}

// ============================================================================
// Donation Section Content
// ============================================================================

export const donationContent: DonationContent = {
  title: 'Support Our Mission',
  description:
    'Your support helps us advocate for legislative change, raise public awareness, conduct research, and provide resources to families navigating custody challenges. Every contribution makes a meaningful difference in the lives of children and parents.',
  disclaimer: taxExemptDonationDisclaimer,
  impactStatement:
    'Every contribution brings us closer to a world where every child can maintain meaningful relationships with both parents.',
  donationOptions: [
    {
      id: 'one-time',
      type: 'one-time',
      title: 'One-Time Donation',
      description: 'Make a single contribution to support our advocacy efforts',
      suggestedAmounts: [25, 50, 100, 250, 500],
      isAvailable: true, // Enabled with Zeffy integration
    },
    {
      id: 'monthly',
      type: 'monthly',
      title: 'Monthly Giving',
      description: 'Provide ongoing support with a recurring monthly donation',
      suggestedAmounts: [10, 25, 50, 100],
      isAvailable: true, // Enabled with Zeffy integration
    },
  ],
}

// ============================================================================
// Why Equal Parenting Section Content
// ============================================================================

export const whyEqualParentingContent: WhyEqualParentingContent = {
  title: 'Why Equal Shared Parenting Matters',
  keyPoints: [
    {
      id: 'children-wellbeing',
      title: "Children's Well-Being",
      description:
        'Research consistently shows that children thrive emotionally, academically, and socially when they maintain strong relationships with both parents. Equal parenting time promotes better mental health outcomes and reduces behavioral problems.',
      icon: 'FamilyRestroom',
      statistic: childMentalHealthStatistic,
    },
    {
      id: 'social-impact',
      title: 'Positive Social Outcomes',
      description:
        'Families with equal custody arrangements experience lower conflict levels, reduced poverty rates, and better overall family stability. This creates healthier communities and reduces the social costs of family breakdown.',
      icon: 'TrendingUp',
      statistic: povertyReductionStatistic,
    },
    {
      id: 'family-relationships',
      title: 'Strengthening Family Bonds',
      description:
        'Equal shared parenting fosters deeper, more meaningful relationships between children and both parents. When children spend substantial time with each parent, they develop secure attachments that last a lifetime.',
      icon: 'Favorite',
    },
    {
      id: 'legislative-need',
      title: 'The Need for Legislative Change',
      description:
        'Current family law often defaults to sole custody arrangements that harm children and families. We advocate for legislation that presumes equal shared parenting is in the best interest of children when both parents are fit, willing, and able.',
      icon: 'Gavel',
    },
  ],
  ctaText: 'Learn More About Our Research',
  ctaLink: '#resources',
}

// ============================================================================
// Resources Section Content
// ============================================================================

export const resourcesContent: ResourcesContent = {
  title: 'Resources & Information',
  categories: [
    {
      id: 'articles',
      title: 'Articles',
      description:
        'Educational content, news, and expert perspectives on equal shared parenting and family law reform',
      icon: 'Article',
      status: 'coming-soon',
    },
    {
      id: 'research',
      title: 'Research & Statistics',
      description:
        'Academic studies, data, and research findings supporting equal parenting outcomes for children',
      icon: 'Science',
      status: 'available',
      link: '/research',
    },
    {
      id: 'title-iv-d',
      title: 'Title IV-D Information',
      description:
        'Understanding the federal child support enforcement system and its impact on families',
      icon: 'AccountBalance',
      status: 'coming-soon',
    },
    {
      id: 'giving-back',
      title: 'Giving Back Programs',
      description:
        'Support programs, resources, and opportunities for non-custodial parents to give back',
      icon: 'VolunteerActivism',
      status: 'coming-soon',
    },
  ],
}

// ============================================================================
// Community Section Content
// ============================================================================

export const communityContent: CommunityContent = {
  title: 'Join Our Community',
  socialLinks: [
    {
      platform: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=100066880196187',
      icon: 'Facebook',
      label: 'Visit our Facebook page',
    },
    // Future: Twitter, Instagram, LinkedIn as accounts are created
  ],
  stats: {
    subscribers: 1250, // Email list count - updated manually
    followers: 3400, // Social media total - updated manually
    statesRepresented: 42, // Geographic reach - updated manually
  },
  testimonials: [], // Optional: Will be added in Phase 2
}

// ============================================================================
// Image Carousel Section Content
// ============================================================================

export const imageCarouselContent: ImageCarouselContent = {
  title: 'Champions for Children',
  subtitle:
    'NCESP Officially Launched at the Washington D.C. Capitol Hill Club on November 4, 2025',
  images: [
    {
      id: 'image-1',
      src: image1,
      caption: 'Board of Directors at the Kickoff Reception in DC',
      alt: 'Board of Directors at the Kickoff Reception in DC',
    },
    {
      id: 'image-2',
      src: image2,
      caption:
        'Congressman Andy Biggs, AZ receives the 1st Annual Champion for the Children Award',
      alt: 'Congressman Andy Biggs, AZ receives the 1st Annual Champion for the Children Award',
    },
    {
      id: 'image-3',
      src: image3,
      caption: 'Congressman Nick Besich, AK',
      alt: 'Congressman Nick Besich, AK',
    },
    {
      id: 'image-4',
      src: image4,
      caption: 'Congressman Eric Burlison, MO',
      alt: 'Congressman Eric Burlison, MO',
    },
    {
      id: 'image-5',
      src: image5,
      caption: 'Board of Directors',
      alt: 'Board of Directors',
    },
    {
      id: 'image-6',
      src: image6,
      caption: 'Honorable Bob McEwen',
      alt: 'Honorable Bob McEwen',
    },
    {
      id: 'image-7',
      src: image7,
      caption:
        'MO Rep Jamie Ray Gragg, Congressman Andy Biggs AZ, PA Rep Jamie Flick',
      alt: 'MO Rep Jamie Ray Gragg, Congressman Andy Biggs AZ, PA Rep Jamie Flick',
    },
    {
      id: 'image-8',
      src: image8,
      caption: 'Congressman Andy Biggs AZ',
      alt: 'Congressman Andy Biggs AZ',
    },
    {
      id: 'image-9',
      src: image9,
      caption: 'MO State Rep Jamie Ray Gragg',
      alt: 'MO State Rep Jamie Ray Gragg',
    },
    {
      id: 'image-10',
      src: image10,
      caption: 'NCESP Leadership Breakfast meeting in DC.',
      alt: 'NCESP Leadership Breakfast meeting in DC.',
    },
  ],
  autoPlayInterval: 5000,
  transitionDuration: 600,
  autoPlayEnabled: true,
}

// ============================================================================
// Footer Section Content
// ============================================================================

export const footerContent: FooterContent = {
  contactInfo: {
    email: 'info@ncesp.org',
    socialLinks: [
      {
        platform: 'Facebook',
        url: 'https://www.facebook.com/profile.php?id=100066880196187',
        icon: 'Facebook',
        label: 'Follow us on Facebook',
      },
      // Future: Additional platforms as accounts are created
    ],
  },
  quickLinks: [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Leadership', href: '/leadership' },
    { label: 'Contact', href: '/contact' },
    { label: 'Donate', href: '/donate' },
  ],
  issueLinks: [
    { label: 'Child Support Reform', href: '/issues/child-support-reform' },
    { label: 'Equal Shared Parenting', href: '/issues/equal-shared-parenting' },
  ],
  copyrightYear: 2024,
  organizationName: 'National Council for Equal Shared Parenting',
  legalLinks: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    // Future: Terms of Use
  ],
  legalInfo: legalOrganizationInfo,
}
